import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import { api } from '@/lib/api';
import { useMonthStore } from '@/lib/monthStore';
import { usePeriodStore, monthIdsQuery } from '@/lib/periodStore';
import { GlassCard } from '@/components/ui/GlassCard';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { StatusBadge, type SeverityLevel } from '@/components/ui/StatusBadge';
import { cn } from '@/lib/cn';
import { formatCurrency, formatDate, formatPercent, formatRelative } from '@/lib/format';
import type { Anomaly, AnomalyAggregated, ExcludedMonth, MonthSummary } from '@/types';

const SEVERITY_ORDER: Record<SeverityLevel, number> = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};

const MONTH_NAMES = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

const TREND_COLORS: Record<string, string> = {
  UP: '#ef4444',
  DOWN: '#a3e635',
  FLAT: '#9ca3af',
};

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: '#ef4444',
  HIGH: '#fb923c',
  MEDIUM: '#ff8a65',
  LOW: '#22d3ee',
};

const TOOLTIP_STYLE = {
  background: '#0a0a0a',
  border: '2px solid #ff8a65',
  borderRadius: 0,
  boxShadow: '4px 4px 0 0 #000000',
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: '12px',
  color: '#f5f5f5',
};

export default function Anomalies() {
  const selectedMonthId = useMonthStore((s) => s.selectedMonthId);
  const monthIds = usePeriodStore((s) => s.monthIds);
  const isMultiMonth = monthIds.length > 1;

  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [aggData, setAggData] = useState<AnomalyAggregated | null>(null);
  const [aggMonths, setAggMonths] = useState<MonthSummary[]>([]);
  const [excludedMonths, setExcludedMonths] = useState<ExcludedMonth[]>([]);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [filter, setFilter] = useState<{
    severity?: SeverityLevel;
    reviewed?: 'all' | 'pending' | 'reviewed';
  }>({ reviewed: 'pending' });

  const fetchAnomalies = async () => {
    if (monthIds.length === 0) return;
    setLoading(true);
    setExcludedMonths([]);
    try {
      if (isMultiMonth) {
        const q = monthIdsQuery(monthIds);
        const res = await api.get<{
          months: MonthSummary[];
          excluded: ExcludedMonth[];
          aggregated: AnomalyAggregated;
        }>(`/aggregate/anomalies?monthIds=${q}`);
        setAggData(res.aggregated);
        setAggMonths(res.months);
        setExcludedMonths(res.excluded);
        setAnomalies([]);
      } else {
        const id = selectedMonthId || monthIds[0];
        if (!id) {
          setLoading(false);
          return;
        }
        const params = new URLSearchParams();
        params.set('monthId', id);
        if (filter.severity) params.set('severity', filter.severity);
        if (filter.reviewed === 'reviewed') params.set('reviewed', 'true');
        if (filter.reviewed === 'pending') params.set('reviewed', 'false');
        params.set('limit', '200');

        const data = await api.get<Anomaly[]>(`/anomalies?${params.toString()}`);
        setAnomalies(
          [...data].sort(
            (a, b) =>
              SEVERITY_ORDER[a.severity as SeverityLevel] -
              SEVERITY_ORDER[b.severity as SeverityLevel],
          ),
        );
        setAggData(null);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnomalies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonthId, monthIds, isMultiMonth, filter]);

  const runDetection = async () => {
    if (isMultiMonth) {
      toast.error('La detección se ejecuta por mes individual. Cambia a un solo mes.');
      return;
    }
    if (!selectedMonthId) return;
    setDetecting(true);
    try {
      const res = await api.post<{ daysProcessed: number; anomaliesFound: number }>(
        `/anomalies/detect/${selectedMonthId}`,
      );
      toast.success(`Detectadas ${res.anomaliesFound} anomalías en ${res.daysProcessed} días`);
      fetchAnomalies();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    } finally {
      setDetecting(false);
    }
  };

  const markReviewed = async (id: string) => {
    try {
      await api.patch(`/anomalies/${id}/review`, {});
      toast.success('Marcada como revisada');
      fetchAnomalies();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    }
  };

  if (monthIds.length === 0) {
    return (
      <>
        <PageHeader tag="// MÓDULO :: 04" title="Anomalías" subtitle="Detección automática de eventos operativos" />
        <EmptyState
          icon="warning"
          title="Sin mes seleccionado"
          description="Carga un archivo Excel para detectar anomalías."
        />
      </>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <span className="material-symbols-outlined text-5xl text-primary animate-spin">
          progress_activity
        </span>
      </div>
    );
  }

  if (!isMultiMonth) {
    const counts = {
      critical: anomalies.filter((a) => a.severity === 'CRITICAL').length,
      high: anomalies.filter((a) => a.severity === 'HIGH').length,
      medium: anomalies.filter((a) => a.severity === 'MEDIUM').length,
      low: anomalies.filter((a) => a.severity === 'LOW').length,
    };

    return (
      <>
        <PageHeader
          tag="// MÓDULO :: 04 / AUDITORÍA"
          title="Anomalías"
          subtitle={`${anomalies.length} eventos encontrados`}
          action={
            <button onClick={runDetection} disabled={detecting} className="kinetic-button">
              {detecting && (
                <span className="material-symbols-outlined animate-spin">progress_activity</span>
              )}
              <span className="material-symbols-outlined">play_arrow</span>
              Re-detectar
            </button>
          }
        />

        <section className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-6">
          <CountBox label="Críticas" count={counts.critical} variant="error" />
          <CountBox label="Altas" count={counts.high} variant="warning" />
          <CountBox label="Medias" count={counts.medium} variant="primary" />
          <CountBox label="Bajas" count={counts.low} variant="tertiary" />
        </section>

        <div className="flex flex-wrap gap-2 mb-6">
          {(['all', 'pending', 'reviewed'] as const).map((opt) => (
            <button
              key={opt}
              onClick={() => setFilter((f) => ({ ...f, reviewed: opt }))}
              className={cn(
                'px-4 py-2 font-bold uppercase tracking-widest text-xs border-2 cursor-pointer transition-colors duration-150',
                filter.reviewed === opt
                  ? 'bg-primary text-black border-primary'
                  : 'bg-surface text-text-muted border-outline hover:border-primary hover:text-text-bright',
              )}
            >
              {opt === 'all' ? 'Todas' : opt === 'pending' ? 'Pendientes' : 'Revisadas'}
            </button>
          ))}
        </div>

        {anomalies.length === 0 ? (
          <EmptyState
            icon="check_circle"
            title="Sin anomalías"
            description="No hay anomalías pendientes para este filtro."
          />
        ) : (
          <div className="space-y-3">
            {anomalies.map((a) => (
              <GlassCard
                key={a.id}
                variant="kinetic"
                padding="md"
                rounded="none"
                className={cn(
                  a.reviewed && 'opacity-60',
                  a.severity === 'CRITICAL' && 'border-error',
                  a.severity === 'HIGH' && 'border-warning',
                )}
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <StatusBadge severity={a.severity as SeverityLevel} />
                      <span className="font-mono text-xs uppercase tracking-widest text-secondary border-2 border-outline px-2 py-0.5">
                        {a.type}
                      </span>
                      {a.skuCode && (
                        <span className="font-mono text-xs uppercase tracking-widest text-text-bright border-2 border-outline px-2 py-0.5">
                          {a.skuCode}
                        </span>
                      )}
                      <span className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
                        Día {a.day.dayOfMonth} · {formatDate(a.day.date)}
                      </span>
                    </div>
                    <p className="font-bold text-text-bright text-sm font-mono uppercase tracking-widest">
                      {a.message}
                    </p>
                    <p className="text-text-muted text-sm mt-1 font-mono">
                      // {a.recommendation}
                    </p>
                    <div className="flex gap-4 mt-3 font-mono text-[10px] uppercase tracking-widest text-text-muted border-t-2 border-outline pt-2">
                      {a.expectedValue !== 0 && (
                        <span>ESP: {formatCurrency(a.expectedValue)}</span>
                      )}
                      {a.actualValue !== 0 && (
                        <span>REAL: {formatCurrency(a.actualValue)}</span>
                      )}
                      {a.difference !== 0 && (
                        <span className="font-bold text-primary">
                          Δ {formatCurrency(a.difference)}
                        </span>
                      )}
                      <span>· {formatRelative(a.detectedAt)}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {!a.reviewed && (
                      <button
                        onClick={() => markReviewed(a.id)}
                        className="kinetic-button-cyan"
                      >
                        <span className="material-symbols-outlined text-base">check</span>
                        Marcar
                      </button>
                    )}
                    {a.reviewed && (
                      <span className="kinetic-badge-lime">
                        <span className="material-symbols-outlined text-base">verified</span>
                        Revisada
                      </span>
                    )}
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        )}
      </>
    );
  }

  if (!aggData) return null;

  const reviewRate =
    aggData.totalCount > 0 ? (aggData.reviewedCount / aggData.totalCount) * 100 : 0;

  return (
    <>
      <PageHeader
        tag="// MÓDULO :: 04 / AUDITORÍA"
        title="Anomalías"
        subtitle={`${aggData.totalCount} eventos en ${aggMonths.length} ${aggMonths.length === 1 ? 'mes' : 'meses'} · ${aggData.reviewedCount} revisadas`}
      />

      {excludedMonths.length > 0 && (
        <div className="mb-6 bg-surface-container border-2 border-warning shadow-[3px_3px_0_0_#000000] p-4 flex items-start gap-3">
          <span className="material-symbols-outlined text-warning mt-0.5">warning</span>
          <div className="flex-1">
            <p className="font-bold text-text-bright font-mono uppercase tracking-widest text-sm">
              ⚠ {excludedMonths.length} mes(es) excluido(s) por baja calidad
            </p>
            <ul className="text-text-muted mt-2 text-xs space-y-1 list-disc list-inside font-mono">
              {excludedMonths.map((m) => (
                <li key={m.monthId}>
                  {m.fileName} — {m.reason}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <section className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-6">
        <CountBox label="Total" count={aggData.totalCount} variant="primary" />
        <CountBox label="Pendientes" count={aggData.pendingCount} variant="error" />
        <CountBox
          label="Revisadas"
          count={aggData.reviewedCount}
          variant="tertiary"
          sub={`${formatPercent(reviewRate)} revisión`}
        />
        <CountBox label="Tipos únicos" count={aggData.byType.length} variant="warning" />
      </section>

      {aggData.totalCount === 0 ? (
        <EmptyState
          icon="check_circle"
          title="Sin anomalías en el período"
          description="No se han detectado anomalías. Ejecuta la detección en cada mes individual para generar datos."
        />
      ) : (
        <>
          <div className="grid grid-cols-12 gap-5 mb-6">
            <GlassCard
              variant="kinetic"
              padding="md"
              rounded="none"
              className="col-span-12 lg:col-span-7"
            >
              <div className="mb-4 pb-3 border-b-2 border-outline">
                <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                  // FREQ_BY_TYPE
                </p>
                <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                  Frecuencia por Tipo
                </h3>
              </div>
              <ResponsiveContainer
                width="100%"
                height={Math.max(220, aggData.byType.length * 50)}
              >
                <BarChart
                  data={aggData.byType.map((t) => ({
                    name: t.type,
                    count: t.count,
                    avgPerMonth: t.avgPerMonth,
                    trend: t.trend,
                  }))}
                  layout="vertical"
                  margin={{ left: 20, right: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis
                    type="number"
                    stroke="#9ca3af"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#525252' }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    stroke="#9ca3af"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#525252' }}
                    width={150}
                  />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="count" stroke="#000000" strokeWidth={2}>
                    {aggData.byType.map((t, i) => (
                      <Cell key={i} fill={TREND_COLORS[t.trend] || '#ff8a65'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 flex gap-4 text-xs font-mono uppercase tracking-widest">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-error" /> Alza
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-tertiary" /> Baja
                </span>
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-text-muted" /> Estable
                </span>
              </div>
            </GlassCard>

            <GlassCard
              variant="kinetic"
              padding="md"
              rounded="none"
              className="col-span-12 lg:col-span-5"
            >
              <div className="mb-4 pb-3 border-b-2 border-outline">
                <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                  // SEVERITY_BREAKDOWN
                </p>
                <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                  Severidad por Tipo
                </h3>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={aggData.byType.map((t) => ({
                    name: t.type,
                    CRITICAL: t.severityBreakdown.CRITICAL,
                    HIGH: t.severityBreakdown.HIGH,
                    MEDIUM: t.severityBreakdown.MEDIUM,
                    LOW: t.severityBreakdown.LOW,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis
                    dataKey="name"
                    stroke="#9ca3af"
                    fontSize={9}
                    tickLine={false}
                    axisLine={{ stroke: '#525252' }}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={{ stroke: '#525252' }} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Legend
                    wrapperStyle={{
                      fontSize: '10px',
                      fontFamily: 'JetBrains Mono, monospace',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                    }}
                  />
                  <Bar dataKey="CRITICAL" stackId="a" fill={SEVERITY_COLORS.CRITICAL} stroke="#000" />
                  <Bar dataKey="HIGH" stackId="a" fill={SEVERITY_COLORS.HIGH} stroke="#000" />
                  <Bar dataKey="MEDIUM" stackId="a" fill={SEVERITY_COLORS.MEDIUM} stroke="#000" />
                  <Bar dataKey="LOW" stackId="a" fill={SEVERITY_COLORS.LOW} stroke="#000" />
                </BarChart>
              </ResponsiveContainer>
            </GlassCard>
          </div>

          {aggData.byMonth.length > 1 && (
            <GlassCard variant="kinetic" padding="md" rounded="none" className="mb-6">
              <div className="mb-4 pb-3 border-b-2 border-outline">
                <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                  // MONTHLY_TREND
                </p>
                <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                  Tendencia Mensual
                </h3>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart
                  data={aggData.byMonth.map((m) => ({
                    label: `${MONTH_NAMES[m.month - 1]} ${String(m.year).slice(-2)}`,
                    detectadas: m.count,
                    revisadas: m.reviewedCount,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis
                    dataKey="label"
                    stroke="#9ca3af"
                    fontSize={11}
                    tickLine={false}
                    axisLine={{ stroke: '#525252' }}
                  />
                  <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={{ stroke: '#525252' }} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Legend
                    wrapperStyle={{
                      fontSize: '10px',
                      fontFamily: 'JetBrains Mono, monospace',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="detectadas"
                    stroke="#ff8a65"
                    strokeWidth={3}
                    dot={{ r: 5, fill: '#ff8a65', stroke: '#000', strokeWidth: 2 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="revisadas"
                    stroke="#a3e635"
                    strokeWidth={3}
                    dot={{ r: 5, fill: '#a3e635', stroke: '#000', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </GlassCard>
          )}

          <div className="grid grid-cols-12 gap-5">
            {aggData.mostCommonRecommendation && (
              <GlassCard
                variant="glow"
                padding="md"
                rounded="none"
                className="col-span-12 lg:col-span-5"
              >
                <div className="mb-4 pb-3 border-b-2 border-primary">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-primary">
                    // TOP_RECOMMENDATION
                  </p>
                  <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                    Recomendación Frecuente
                  </h3>
                </div>
                <div className="bg-surface border-2 border-warning p-5">
                  <span className="material-symbols-outlined text-warning text-3xl align-middle mr-2">
                    lightbulb
                  </span>
                  <p className="inline text-text-bright font-mono">
                    {aggData.mostCommonRecommendation}
                  </p>
                </div>
              </GlassCard>
            )}

            <GlassCard
              variant="kinetic"
              padding="md"
              rounded="none"
              className="col-span-12 lg:col-span-7 overflow-hidden"
            >
              <div className="mb-4 pb-3 border-b-2 border-outline">
                <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                  // TYPE_SUMMARY
                </p>
                <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                  Resumen por Tipo
                </h3>
              </div>
              <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b-2 border-outline">
                      <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted">
                        Tipo
                      </th>
                      <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-right">
                        Total
                      </th>
                      <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-right">
                        x Mes
                      </th>
                      <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-right">
                        CRIT
                      </th>
                      <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-right">
                        HIGH
                      </th>
                      <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-right">
                        MED
                      </th>
                      <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-right">
                        LOW
                      </th>
                      <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-text-muted text-center">
                        Tendencia
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {aggData.byType.map((t) => (
                      <tr key={t.type} className="border-b border-outline/50">
                        <td className="px-3 py-2 font-medium text-sm text-text-bright">{t.type}</td>
                        <td className="px-3 py-2 num font-bold text-right text-text-bright">{t.count}</td>
                        <td className="px-3 py-2 num text-right text-text-muted">
                          {t.avgPerMonth.toFixed(1)}
                        </td>
                        <td className="px-3 py-2 num text-right text-error">
                          {t.severityBreakdown.CRITICAL}
                        </td>
                        <td className="px-3 py-2 num text-right text-warning">
                          {t.severityBreakdown.HIGH}
                        </td>
                        <td className="px-3 py-2 num text-right text-primary">
                          {t.severityBreakdown.MEDIUM}
                        </td>
                        <td className="px-3 py-2 num text-right text-secondary">
                          {t.severityBreakdown.LOW}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest font-bold border-2 border-black"
                            style={{ background: TREND_COLORS[t.trend], color: '#000' }}
                          >
                            <span className="material-symbols-outlined text-sm">
                              {t.trend === 'UP'
                                ? 'trending_up'
                                : t.trend === 'DOWN'
                                  ? 'trending_down'
                                  : 'trending_flat'}
                            </span>
                            {t.trend}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </GlassCard>
          </div>
        </>
      )}
    </>
  );
}

function CountBox({
  label,
  count,
  variant,
  sub,
}: {
  label: string;
  count: number;
  variant: 'primary' | 'tertiary' | 'warning' | 'error';
  sub?: string;
}) {
  const colorMap = {
    primary: 'border-primary text-primary',
    tertiary: 'border-tertiary text-tertiary',
    warning: 'border-warning text-warning',
    error: 'border-error text-error',
  };
  return (
    <div
      className={cn(
        'bg-surface border-2 p-5 shadow-[3px_3px_0_0_#000000]',
        colorMap[variant],
      )}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="material-symbols-outlined text-2xl">flag</span>
        <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
          {label}
        </p>
      </div>
      <p className="font-condensed text-5xl text-text-bright num leading-none">{count}</p>
      {sub && (
        <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-2">
          {sub}
        </p>
      )}
    </div>
  );
}
